// @ts-nocheck

import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { BASE_URL } from "../Constants/BaseUrl";

import axios from "axios";
import { toast } from "react-toastify";
import { fetchUserOwnerProfile } from "./postsSlice";

// Define Athlete interface
export interface Athlete {
  sport_id: number;
  position: string;
  height_cm: number;
  weight_kg: number;
  dominant_hand: string;
  years_of_experience: number;
  achievements: string;
}

// Define the UserState interface
interface UserState {
  user_id: number | null;
  username: string;
  email: string;
  role: string;
  profile_picture: string | null;
  token: string | null;
  parent_admin_id: number | null;
  is_suspended: boolean;
  created_at: string;
  updated_at: string;
  isLoggedIn: boolean;
  loading: boolean;
  profileCompleted: string;
  error: string | null;
  athlete_info: Athlete | null; // Updated to store athlete details
  profileVisibility: "private" | "public";
}

const initialState: UserState = {
  user_id: null,
  username: "",
  email: "",
  role: "",
  profile_picture: null,
  token: null,
  parent_admin_id: null,
  is_suspended: false,
  created_at: "",
  updated_at: "",
  isLoggedIn: false,
  loading: false,
  error: null,
  athlete_info: null, // Initially set to null
  profileCompleted: "no",
  profileVisibility: "private",
};

// 🔐 Async thunk to handle user registration
export const registerUser = createAsyncThunk(
  "user/register",
  async (
    {
      email,
      password,
      role,
    }: {
      email: string;
      password: string;
      role: string;
    },
    thunkAPI
  ) => {
    try {
      let username = email.split("@")[0];
      const formData = new FormData();
      formData.append("username", username);
      formData.append("email", email);
      formData.append("password", password);
      formData.append("role", role); // ensure role is appended

      const endpoint = `${BASE_URL}auth/register`;

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          Accept: "application/json",
          // Don't set Content-Type explicitly for FormData (fetch will handle it)
        },
        body: formData,
      });

      const result = await response.json();

      if (!response.ok || result.success === false) {
        const message = result.message || "Registration failed";
        throw new Error(message);
      }

      console.log(result, "RegisterApi");

      // toast.success("Registration successful!");

      return result;
    } catch (error: any) {
      // toast.warn("Registration failed");

      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const toggleProfileVisibility = createAsyncThunk(
  "user/toggleProfileVisibility",
  async (_: void, { getState, rejectWithValue }) => {
    try {
      const state = getState() as { user: UserState };
      const token = state.user.token;

      if (!token) throw new Error("Authentication token missing");

      const formData = new FormData();
      formData.append("token", token);

      const response = await fetch(`${BASE_URL}user/toggle-visibility`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const result = await response.json();
      console.log("toggleProfileVisibility result:", result);

      if (!response.ok || result.success === false) {
        throw new Error(result.message || "Failed to toggle visibility");
      }

      const apiValue = result.data?.profile_visibility?.toLowerCase();
      if (!["public", "private"].includes(apiValue ?? "")) {
        throw new Error("Invalid visibility value from server");
      }

      // Return only the new visibility
      return apiValue as "public" | "private";
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to update profile visibility"
      );
    }
  }
);

export const saveAthleticInfo = createAsyncThunk(
  "user/saveAthleticInfo",
  async (
    {
      athlete_id,
      username,
      fcm_token,
      first_name,
      last_name,
      dob,
      gender,
      current_school,
      sport_id,
      position,
      school_year,
      height,
      weight,
      division,
    }: {
      athlete_id: string;
      username: string;
      fcm_token: string;
      first_name: string;
      last_name: string;
      dob: string;
      gender: string;
      current_school: string;
      sport_id: string;
      position: string;
      school_year: string;
      height: string;
      weight: string;
      division: string;
    },
    thunkAPI
  ) => {
    try {
      const formData = new FormData();
      formData.append("athlete_id", athlete_id);
      formData.append("username", username);
      formData.append("loginKey", "letMeLoginWithoutPassword000999");
      formData.append("fcm_token", fcm_token);
      formData.append("first_name", first_name);
      formData.append("last_name", last_name);
      formData.append("dob", dob);
      formData.append("gender", gender);
      formData.append("current_school", current_school);
      formData.append("sport_id", sport_id);
      formData.append("position", position);
      formData.append("school_year", school_year);
      formData.append("height", height);
      formData.append("weight", weight);
      formData.append("division", division);

      const response = await axios.post(
        `${BASE_URL}athlete/addStudentInfo`,
        formData,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );

      console.log(response, "REsponseFaraz");
      if (response.status !== 200 || !response.data.success) {
        throw new Error(response.data.message || "Failed to save athlete info");
      }

      // toast.success("Athletic info saved successfully!");

      return {
        profileCompleted: response.data?.data?.profileCompleted,
        user_id: response.data.data.user_id,
        username: response.data.data.username,
        email: response.data.data.email,
        role: response.data.data.role,
        profile_picture: response.data.data.profile_picture,
        parent_admin_id: response.data.data.parent_admin_id,
        is_suspended: response.data.data.is_suspended,
        created_at: response.data.data.created_at,
        updated_at: response.data.data.updated_at,
        token: response.data.data.token,
        athlete_info: response.data.data.athlete_info || null,
      };
    } catch (error: any) {
      // toast.warn("Error");

      return thunkAPI.rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

export const getUserNotifications = createAsyncThunk(
  "user/getNotifications",
  async (token: string, thunkAPI) => {
    try {
      const response = await fetch(`${BASE_URL}user/notifications`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch notifications");
      }

      const result = await response.json();

      return result.data; // Assuming the response has { success: true, data: [...] }
    } catch (error: any) {
      showMessage({
        message: error.message || "Failed to load notifications",
        type: "danger",
      });
      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

export const blockUser = createAsyncThunk<
  any, // Use any for return type to avoid defining BlockUserResponse
  { userId: number },
  { state: RootState; rejectValue: string }
>("user/blockUser", async ({ userId }, thunkApi) => {
  try {
    const state = thunkApi.getState();
    const token = state.user.token;

    console.log("Token used for blockUser:", token);
    console.log("Blocking/Unblocking userId:", userId);

    const formData = new FormData();
    formData.append("blocked_user_id", userId.toString());

    const response = await fetch(`${BASE_URL}/user/toggle-block`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token || ""}`,
      },
      body: formData,
    });

    const data = await response.json();
    console.log("Block response: ", data);

    if (!response.ok) {
      throw new Error(data.error || "Failed to toggle block status");
    }

    // if (data.error === "Invalid token") {
    //   alert("Session Expired!");
    //   thunkApi.dispatch(logoutUser());
    //   return thunkApi.rejectWithValue("Session Expired");
    // }

    return data;
  } catch (error: any) {
    console.error("Error in blockUser:", error.message);
    return thunkApi.rejectWithValue(
      error.message || "Failed to toggle block status"
    );
  }
});

export const getBlockedUsersList = createAsyncThunk(
  "user/getBlockedUsersList",
  async (_, thunkApi) => {
    try {
      const state = thunkApi.getState();
      const token = state.user.token;

      const response = await fetch(`${BASE_URL}/user/blocked-users`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token || ""}`,
          "Content-Type": "application/json",
        },
      });

      const data = await response.json();
      console.log("Blocked users response: ", data);

      if (!response.ok) {
        throw new Error(data.error || "Failed to fetch blocked users");
      }

      if (data.error === "Invalid token") {
        alert("Session Expired!");
        thunkApi.dispatch(logoutUser());
      }

      return data.data;
    } catch (error: any) {
      return thunkApi.rejectWithValue(
        error.message || "Failed to fetch blocked users"
      );
    }
  }
);

// 🔐 Async thunk to handle user login
export const loginUser = createAsyncThunk(
  "user/login",
  async (
    {
      email,
      password,
      fcm_token,
    }: { email: string; password: string; fcm_token: string },
    thunkAPI
  ) => {
    try {
      const response = await fetch(`${BASE_URL}auth/login`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password,
          fcm_token,
        }),
      });

      if (!response.ok) {
        throw new Error("Login failed");
      }

      const result = await response.json();

      console.log(result);
      // toast.success("Login successful!");

      // After login, fetch the user's profile to get athlete_info
      const profile = await thunkAPI.dispatch(getProfile(result.data.token));

      return {
        profileCompleted: result.data?.profileCompleted,
        user_id: result.data.user_id,
        username: result.data.username,
        email: result.data.email,
        role: result.data.role,
        profile_picture: result.data.profile_picture,
        parent_admin_id: result.data.parent_admin_id,
        is_suspended: result.data.is_suspended,
        created_at: result.data.created_at,
        updated_at: result.data.updated_at,
        token: result.data.token,
        athlete_info: profile.payload, // Athlete info from profile API
      };
    } catch (error: any) {
      // toast.warn("Login failed");

      return thunkAPI.rejectWithValue(error.message);
    }
  }
);

// 🔐 Async thunk to fetch user profile (athlete_info)

export const getProfile = createAsyncThunk(
  "user/getProfile",
  async (token: string, thunkAPI) => {
    if (!token) {
      console.error("No token found!");
      return thunkAPI.rejectWithValue("Missing token");
    }

    try {
      const response = await axios.get(`${BASE_URL}/user/get-profile`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`, // Send token in the Authorization header
        },
      });

      if (response.status !== 200) {
        throw new Error("Failed to fetch profile");
      }

      if (
        response.data &&
        response.data.data &&
        response.data.data.athlete_info
      ) {
        return response.data.data.athlete_info; // Return athlete_info if available
      } else {
        return null; // Return null if athlete_info is not available
      }
    } catch (error: any) {
      console.error("Error fetching profile:", error);
      // Check if the error response exists and contains a message
      const errorMessage = error.response?.data?.error || error.message;
      return thunkAPI.rejectWithValue(errorMessage);
    }
  }
);

export const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<UserState>) => {
      state.user_id = action.payload.user_id;
      state.username = action.payload.username;
      state.email = action.payload.email;
      state.role = action.payload.role;
      state.profile_picture = action.payload.profile_picture;
      state.token = action.payload.token;
      state.parent_admin_id = action.payload.parent_admin_id;
      state.is_suspended = action.payload.is_suspended;
      state.created_at = action.payload.created_at;
      state.updated_at = action.payload.updated_at;
      state.isLoggedIn = true;
      state.athlete_info = action.payload.athlete_info || null; // Store athlete_info

      // Save user data to AsyncStorage
      localStorage.setItem("user", JSON.stringify(state));
      localStorage.setItem("token", state.token || "");
    },

    setProfileCompleted: (state, action) => {
      state.profileCompleted = action.payload;
    },

    storeUserData: (state, action) => {
      const payload = action.payload;
      state.user_id = payload.user_id;
      state.profileCompleted = payload.profileCompleted;
      state.username = payload.username;
      state.email = payload.email;
      state.role = payload.role;
      state.profile_picture = payload.profile_picture;
      state.token = payload.token;
      state.parent_admin_id = payload.parent_admin_id;
      state.is_suspended = payload.is_suspended;
      state.created_at = payload.created_at;
      state.updated_at = payload.updated_at;
      state.isLoggedIn = true;
      state.athlete_info = payload.athlete_info || null;
    },
    updateUserProfilePicture(state, action: PayloadAction<string>) {
      state.profile_picture = action.payload;
    },
    logoutUser: (state) => {
      state.user_id = null;
      state.username = "";
      state.email = "";
      state.role = "";
      state.profile_picture = null;
      state.token = null;
      state.parent_admin_id = null;
      state.is_suspended = false;
      state.created_at = "";
      state.updated_at = "";
      state.isLoggedIn = false;
      state.athlete_info = null; // Clear athlete_info

      // Clear user data from AsyncStorage
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(registerUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(registerUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProfileVisibility.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleProfileVisibility.fulfilled, (state, action) => {
        state.profileVisibility = action.payload;
        localStorage.setItem("user", JSON.stringify(state));
        state.loading = false;
      })
      .addCase(toggleProfileVisibility.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(fetchUserOwnerProfile.fulfilled, (state, action) => {
        const visibility = action.payload?.profile_visibility?.toLowerCase();
        if (["public", "private"].includes(visibility)) {
          state.profileVisibility = visibility as "public" | "private";
        }
        // Optionally update other fields if needed
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload.profileCompleted);
        if (action.payload.profileCompleted !== "no") {
          state.user_id = action.payload.user_id;

          state.email = action.payload.email;
          state.role = action.payload.role;
          state.profile_picture = action.payload.profile_picture;
          state.token = action.payload.token;
          state.parent_admin_id = action.payload.parent_admin_id;
          state.is_suspended = action.payload.is_suspended;
          state.created_at = action.payload.created_at;
          state.updated_at = action.payload.updated_at;
          state.isLoggedIn = true;
          state.athlete_info = action.payload.athlete_info || null; // Store athlete_info.
          state.profileCompleted = action.payload.profileCompleted;
          state.username = action.payload.username;
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getProfile.fulfilled, (state, action) => {
        state.athlete_info = action.payload || null; // Store athlete_info from profile API
      });

    builder
      .addCase(getUserNotifications.pending, (state) => {})
      .addCase(getUserNotifications.fulfilled, (state, action) => {
        // state.notifications = action.payload;
      })
      .addCase(getUserNotifications.rejected, (state, action) => {
        state.error = action.payload;
      });
    builder
      .addCase(saveAthleticInfo.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveAthleticInfo.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(saveAthleticInfo.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    builder
      .addCase(blockUser.pending, (state) => {
        state.loading = true;
        state.error = null;
        console.log("blockUser pending");
      })
      .addCase(blockUser.fulfilled, (state, action) => {
        state.loading = false;
        const message =
          action.payload?.message || "Action completed successfully";
        toast.success(
          message.includes("unblocked") ? "User Unblocked" : "User Blocked",
          {
            description: message,
            type: "success",
            duration: 4000,
            position: "top-right",
          }
        );
      })
      .addCase(blockUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to toggle block status";
        console.log("blockUser rejected:", action.payload);
        toast.error(action.payload || "Failed to toggle block status", {
          position: "top-right",
          autoClose: 4000,
        });
      });

    builder
      .addCase(getBlockedUsersList.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getBlockedUsersList.fulfilled, (state, action) => {
        state.loading = false;
        state.blockedUsers = action.payload;
      })
      .addCase(getBlockedUsersList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error("Failed to fetch blocked users. Please try again.", {
          position: "top-right",
          autoClose: 4000,
        });
      });
  },
});

export const {
  setUser,
  logoutUser,
  setProfileCompleted,
  storeUserData,
  updateUserProfilePicture,
} = userSlice.actions;
export const selectProfileVisibility = (state: { user: UserState }) =>
  state.user.profileVisibility;
export default userSlice.reducer;
